import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { Context } from "../../../../context/Context";
import { translations } from "../../../../utils/lang";
import {
  Circle,
  CircleArrowDown,
  CircleArrowUp,
  Input,
  SaudiRiyal,
  Sigma,
  StarHalf,
} from "../../../ui";
import {
  fetchCompenstationAPI,
  fetchDailyAPI,
} from "../../../../utils/api/operations";
import FilterGroup from "../../../analysis/filter/FilterUI";
import { normalizeIntegrationKey } from "../../../../utils/integrations";

// "yyyy-mm-dd" as a local date, or null when cleared/invalid (a cleared input
// used to reach date-fns `format` as Invalid Date and crash the page).
const parseDayInput = (value) => {
  const [y, m, d] = String(value || "").split("-").map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
};

/* ---------- helpers ---------- */

const aggregate = (list = []) =>
  list.reduce(
    (acc, curr) => {
      acc.count += curr.completed || 0;
      acc.amount += curr.completedAmount || 0;
      return acc;
    },
    { count: 0, amount: 0 },
  );

const aggregateCompensation = (list = []) =>
  list.reduce(
    (acc, curr) => {
      acc.count += 1; // each record = 1 compensation
      acc.amount += curr.orderAmount || 0;
      return acc;
    },
    { count: 0, amount: 0 },
  );

const coloring = (current, prev) => {
  if (current > prev) return "up";
  if (current < prev) return "down";
  if (!prev) return "square";
  return "square";
};

const Arrowing = (current, prev) => {
  if (!prev) return <Circle className="arrowing-icon" />;
  if (current > prev) return <CircleArrowUp className="arrowing-icon" />;
  if (current < prev) return <CircleArrowDown className="arrowing-icon" />;
  return <Circle className="arrowing-icon" />;
};

/* ---------- component ---------- */

export default function DailyOperation({ storeId, appId, from, to, appName }) {
  const { handleTokenExpiration, lang } = useContext(Context);
  const tokenExpiredRef = useRef(handleTokenExpiration);
  tokenExpiredRef.current = handleTokenExpiration;

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const [fromDate, setFromDate] = useState(
    from ? new Date(from) : new Date(today.getFullYear(), today.getMonth(), 1),
  );

  const [toDate, setToDate] = useState(to ? new Date(to) : yesterday);

  useEffect(() => {
    if (from) {
      const newFrom = new Date(from);
      if (!isNaN(newFrom)) setFromDate(newFrom);
    }
  }, [from]);

  useEffect(() => {
    if (to) {
      const newTo = new Date(to);
      if (!isNaN(newTo)) setToDate(newTo);
    }
  }, [to]);

  const year = fromDate.getFullYear();
  const month = fromDate.getMonth() + 1;
  const fromDay = fromDate.getDate();

  const toYear = toDate.getFullYear();
  const toMonth = toDate.getMonth() + 1;
  const toDay = toDate.getDate();

  const [currentData, setCurrent] = useState([]);
  const [filters, setFilters] = useState({});
  const [previousData, setPrevious] = useState([]);
  const [currentComp, setCurrentComp] = useState([]);
  const [previousComp, setPreviousComp] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const branches = useMemo(
    () => [...new Set(currentData?.map((d) => d.branchName))],
    [currentData],
  );

  const handleFilterChange = (category, values) => {
    setFilters((prev) => ({
      ...prev,
      [category]: values,
    }));
  };

  const filteredDaily = useMemo(() => {
    if (!filters.branch || filters.branch.length === 0) return currentData;
    return currentData?.filter((d) => filters.branch.includes(d.branchName));
  }, [currentData, filters]);

  const filteredPrevDaily = useMemo(() => {
    if (!filters.branch || filters.branch.length === 0) return previousData;
    return previousData.filter((d) => filters.branch.includes(d.branchName));
  }, [previousData, filters]);

  const filteredComp = useMemo(() => {
    if (!filters.branch || filters.branch.length === 0) return currentComp;
    return currentComp.filter((d) => filters.branch.includes(d.branchName));
  }, [currentComp, filters]);

  const filteredPrevComp = useMemo(() => {
    if (!filters.branch || filters.branch.length === 0) return previousComp;
    return previousComp.filter((d) => filters.branch.includes(d.branchName));
  }, [previousComp, filters]);

  useEffect(() => {
    const token = localStorage.getItem("auth-token");
    if (!token) {
      tokenExpiredRef.current();
      return undefined;
    }

    // Both reports share the same range; responses for an older range are
    // ignored so a quick date change can't show mismatched numbers.
    let active = true;
    const params = {
      storeId,
      appId,
      appName: normalizeIntegrationKey(appName) || appName,
      year,
      month,
      toYear,
      toMonth,
      from: fromDay,
      to: toDay,
      isLud: normalizeIntegrationKey(appName) === "LUD",
    };

    setLoading(true);
    setLoadFailed(false);
    Promise.allSettled([
      fetchDailyAPI(token, params),
      fetchCompenstationAPI(token, params),
    ]).then(([daily, compensation]) => {
      if (!active) return;
      if (daily.status === "fulfilled") {
        setCurrent(daily.value?.current || []);
        setPrevious(daily.value?.previous || []);
      }
      if (compensation.status === "fulfilled") {
        setCurrentComp(compensation.value?.current || []);
        setPreviousComp(compensation.value?.previous || []);
      }
      const failed =
        daily.status === "rejected" ||
        compensation.status === "rejected" ||
        !daily.value ||
        !compensation.value;
      if (daily.status === "rejected") console.error(daily.reason);
      if (compensation.status === "rejected") console.error(compensation.reason);
      setLoadFailed(failed);
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [storeId, appId, appName, year, month, fromDay, toYear, toMonth, toDay]);

  /* ---------- calculations ---------- */

  // Daily
  const current = useMemo(() => aggregate(filteredDaily), [filteredDaily]);

  const previous = useMemo(
    () => aggregate(filteredPrevDaily),
    [filteredPrevDaily],
  );

  const avg = useMemo(
    () => (current.count ? (current.amount / current.count).toFixed(2) : 0),
    [current],
  );

  const preAvg = useMemo(
    () =>
      previous.count ? (previous.amount / previous.count).toFixed(2) : 0,
    [previous],
  );

  // Compensation
  const compCurrent = useMemo(
    () => aggregateCompensation(filteredComp),
    [filteredComp],
  );

  const compPrevious = useMemo(
    () => aggregateCompensation(filteredPrevComp),
    [filteredPrevComp],
  );

  /* ---------- UI ---------- */

  return (
    <div className="monthly-operation-card" aria-busy={loading || undefined}>
      {/* Header */}
      <div className="monthly-operation-header">
        <div className="monthly-operation-header-row">
          <Input
            type="date"
            value={format(fromDate, "yyyy-MM-dd")}
            onChange={(e) => {
              const next = parseDayInput(e.target.value);
              if (next) setFromDate(next);
            }}
            aria-label={lang === "ar" ? "من تاريخ" : "From date"}
            fullWidth={false}
          />

          <Input
            type="date"
            value={format(toDate, "yyyy-MM-dd")}
            onChange={(e) => {
              const next = parseDayInput(e.target.value);
              if (next) setToDate(next);
            }}
            aria-label={lang === "ar" ? "إلى تاريخ" : "To date"}
            fullWidth={false}
          />
        </div>

        <FilterGroup
          label="Branch"
          options={branches}
          selected={filters?.branch || []}
          onChange={(vals) => handleFilterChange("branch", vals)}
        />
      </div>

      {loadFailed && (
        <p className="monthly-operation-error" role="status">
          {lang === "ar"
            ? "تعذر تحميل بعض بيانات هذه الفترة"
            : "Some data for this period could not be loaded"}
        </p>
      )}

      {/* Orders */}
      <div className="monthly-operation-top">
        <div className="monthly-operation-metric">
          <div className="monthly-operation-label">
            <Sigma className="arrowing-icon" />{" "}
            {translations[lang].Operation.completed}
          </div>

          <span
            className={`monthly-opr-small-value ${coloring(previous.count, current.count)}`}
          >
            {previous.count
              ? (
                  ((current.count - previous.count) / previous.count) *
                  100
                ).toFixed(2)
              : 0}
            %
          </span>
          <div className="monthly-operation-value-container">
            <span className="monthly-operation-value">{current.count}</span>
            <span
              className={`monthly-operation-value ${coloring(previous.count, current.count)}`}
            >
              {Arrowing(current.count, previous.count)}
            </span>
          </div>

          <span className="monthly-opr-small-value">{previous.count}</span>
        </div>

        <div className="monthly-operation-metric">
          <div className="monthly-operation-label">
            <SaudiRiyal className="arrowing-icon" />
            {translations[lang].Operation.completedAmount}
          </div>

          <span
            className={`monthly-opr-small-value ${coloring(previous.amount, current.amount)}`}
          >
            {previous.amount
              ? (
                  ((current.amount - previous.amount) / previous.amount) *
                  100
                ).toFixed(2)
              : 0}
            %
          </span>

          <div className="monthly-operation-value-container">
            <span className="monthly-operation-value">
              {current.amount.toFixed(2)}
            </span>
            <span
              className={`monthly-operation-value ${coloring(previous.amount, current.amount)}`}
            >
              {Arrowing(current.amount, previous.amount)}
            </span>
          </div>

          <span className="monthly-opr-small-value">
            {previous.amount.toFixed(2)}
          </span>
        </div>
      </div>

      <div className="monthly-operation-bottom">
        <div className="monthly-operation-bottom-item">
          <div className="monthly-operation-label">
            <StarHalf className="monthly-operation-value  arrowing-icon" />
            {translations[lang].Operation.avg}:{" "}
            <span className="monthly-operation-value">{avg}</span>
            <span className="monthly-opr-small-value">{preAvg}</span>
            <span
              className={`monthly-operation-value ${coloring(preAvg, avg)}`}
            >
              {Arrowing(avg, preAvg)}
            </span>
            <span
              className={`monthly-opr-small-value ${coloring(preAvg, avg)}`}
            >
              {preAvg ? (((avg - preAvg) / preAvg) * 100).toFixed(2) : 0}%
            </span>
          </div>
        </div>
      </div>

      {/* Compensation */}
      <div className="monthly-operation-top">
        <div className="monthly-operation-metric">
          <div className="monthly-operation-label">
            <Sigma className="arrowing-icon" />
            {translations[lang].Operation.faults}
          </div>

          <span
            className={`monthly-opr-small-value ${coloring(
              compCurrent.count,
              compPrevious.count,
            )}`}
          >
            {compPrevious.count
              ? (
                  ((compCurrent.count - compPrevious.count) /
                    compPrevious.count) *
                  100
                ).toFixed(2)
              : 0}
            %
          </span>

          <div className="monthly-operation-value-container">
            <span className="monthly-operation-value">{compCurrent.count}</span>
            <span
              className={`monthly-operation-value ${coloring(
                compCurrent.count,
                compPrevious.count,
              )}`}
            >
              {Arrowing(compCurrent.count, compPrevious.count)}
            </span>
          </div>

          <span className="monthly-opr-small-value">{compPrevious.count}</span>
        </div>

        <div className="monthly-operation-metric">
          <div className="monthly-operation-label">
            <SaudiRiyal className="arrowing-icon" />{" "}
            {translations[lang].Operation.faultsAmount}
          </div>

          <span
            className={`monthly-opr-small-value ${coloring(
              compCurrent.amount,
              compPrevious.amount,
            )}`}
          >
            {compPrevious.amount
              ? (
                  ((compCurrent.amount - compPrevious.amount) /
                    compPrevious.amount || 0) * 100
                ).toFixed(2)
              : 0}
            %
          </span>

          <div className="monthly-operation-value-container">
            <span className="monthly-operation-value">
              {compCurrent.amount.toFixed(2)}
            </span>
            <span
              className={`monthly-operation-value ${coloring(
                compCurrent.amount,
                compPrevious.amount,
              )}`}
            >
              {Arrowing(compCurrent.amount, compPrevious.amount)}
            </span>
          </div>

          <span className="monthly-opr-small-value">
            {compPrevious.amount.toFixed(2)}
          </span>
        </div>
      </div>

    </div>
  );
}